import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { RadioGroup, RadioGroupItem, Label, Badge, cn } from '@scio3/ui';
import { ADDON_TIER_CATALOG, ADDON_TIER_FEATURE_KEYS } from '../utils/billing-constants';

type Tier = 'standard' | 'pro';

type TierPricing = {
  /** Price per screen per interval, numeric */
  price: number;
  /** Interval label fragment, e.g. "mo" or "yr" */
  intervalSuffix: string;
};

type Props = {
  addonKey: string;
  selectedTier: Tier;
  onTierChange: (tier: Tier) => void;
  pricing: Partial<Record<Tier, TierPricing>>;
  currencySymbol: string;
  /** Tier the account is currently subscribed to — renders the "Current Plan" badge */
  currentTier?: Tier | null;
  disabled?: boolean;
};

/**
 * Standard vs Pro tier radio cards for multi-tier add-ons.
 * Renders null when the add-on has no tier catalog entry, so non-tiered
 * add-ons (aericast, videowall) are unaffected.
 *
 * Matches scio1's subscriptionPlanModal tier selector: two stacked price cards
 * with a "Current Plan" badge on the active tier. scio3 also retains a radio
 * affordance so users can clearly see which tier they're about to apply.
 */
export function AddonTierRadioCards({
  addonKey,
  selectedTier,
  onTierChange,
  pricing,
  currencySymbol,
  currentTier = null,
  disabled = false,
}: Props) {
  const { t } = useTranslation();
  const tiers = ADDON_TIER_CATALOG[addonKey];
  const featureKeys = ADDON_TIER_FEATURE_KEYS[addonKey];

  if (!tiers || !featureKeys) return null;

  const headingId = `addon-tier-cards-heading-${addonKey}`;

  return (
    <div className="space-y-3" data-testid="addon-tier-cards">
      <h3 id={headingId} className="text-xl font-semibold text-center">
        {t('Choose your plan')}
      </h3>
      <RadioGroup
        value={selectedTier}
        onValueChange={(value) => onTierChange(value as Tier)}
        disabled={disabled}
        aria-labelledby={headingId}
        className="grid grid-cols-2 gap-4"
      >
        {tiers.map(({ tier }) => {
          const tierPricing = pricing[tier];
          const bullets = featureKeys[tier] ?? [];
          const isSelected = selectedTier === tier;
          const isCurrent = currentTier === tier;
          const tierLabel = tier === 'pro' ? t('Pro') : t('Standard');

          return (
            <Label
              key={tier}
              htmlFor={`addon-tier-${tier}`}
              data-testid={`addon-tier-card-${tier}`}
              className={cn(
                'rounded-lg border p-5 flex flex-col items-stretch gap-4 transition-colors',
                // scio1 .planboxBodySub has min-height: 400px — we use min-h-[360px]
                // so Standard (3 short bullets) and Pro (2 long bullets) visually align.
                'min-h-[360px]',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-accent/50',
                isSelected && 'border-primary bg-accent',
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-base">{tierLabel}</span>
                  {isCurrent && (
                    <Badge variant="secondary" className="text-xs">
                      {t('Current Plan')}
                    </Badge>
                  )}
                </div>
                <RadioGroupItem
                  value={tier}
                  id={`addon-tier-${tier}`}
                  aria-label={tierLabel}
                  className="mt-1 shrink-0"
                />
              </div>
              {tierPricing && (
                <div className="text-center">
                  <p className="text-3xl font-semibold">
                    {currencySymbol}
                    {tierPricing.price.toFixed(2)}
                  </p>
                  <p className="text-sm text-muted-foreground">{t('per screen/month')}</p>
                </div>
              )}
              {bullets.length > 0 && (
                <ul className="text-sm space-y-2">
                  {bullets.map((key) => (
                    <li key={key} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Label>
          );
        })}
      </RadioGroup>
    </div>
  );
}
